import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { MagnifyingGlass, Sparkle, BellSimpleRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * Admin-facing modal to manage the industries an investor wants alerts for.
 * Pulls the industry list from GET /api/companies/filters/options so any
 * additions/removals made under /admin/filters flow through automatically.
 */
export default function InvestorInterestsDialog({ open, onOpenChange, user, onSaved }) {
  const [allIndustries, setAllIndustries] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ("");
    api.get("/companies/filters/options").then((r) => {
      setAllIndustries(Array.isArray(r.data?.industries) ? r.data.industries : []);
    }).catch(() => setAllIndustries([]));
    setSelected(Array.isArray(user?.interested_industries) ? [...user.interested_industries] : []);
  }, [open, user]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return allIndustries;
    return allIndustries.filter((i) => i.toLowerCase().includes(needle));
  }, [allIndustries, q]);

  const toggle = (industry) => {
    setSelected((cur) => cur.includes(industry) ? cur.filter((x) => x !== industry) : [...cur, industry]);
  };

  const selectAll = () => setSelected([...allIndustries]);
  const clearAll = () => setSelected([]);

  const save = async () => {
    if (!user) return;
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}/interests`, { industries: selected });
      toast.success(`Interests updated for ${user.name || user.email}`);
      onSaved && onSaved(selected);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save interests");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="investor-interests-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2">
            <BellSimpleRinging size={20} weight="fill" className="text-emerald-600" /> Industry alerts
          </DialogTitle>
          <DialogDescription>
            Pick the industries <span className="font-semibold text-foreground">{user?.name || user?.email}</span> wants alerts for. They&apos;ll get an email + in-app notification each time a new company is published in any selected industry.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search industries…"
              className="pl-9 h-10"
              data-testid="interests-search-input"
            />
          </div>

          <div className="flex items-center justify-between text-xs">
            <div className="text-muted-foreground">
              {selected.length} of {allIndustries.length} selected
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={selectAll} className="text-emerald-700 dark:text-emerald-400 font-semibold hover:underline" data-testid="interests-select-all">Select all</button>
              <span className="text-border">·</span>
              <button type="button" onClick={clearAll} className="text-muted-foreground hover:underline font-semibold" data-testid="interests-clear-all">Clear</button>
            </div>
          </div>

          {allIndustries.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No industries defined yet.{" "}
              <Link to="/admin/filters" onClick={() => onOpenChange(false)} className="text-emerald-700 dark:text-emerald-400 font-semibold hover:underline">
                Add industries under Filters
              </Link>
              {" "}first.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto rounded-xl border border-border divide-y divide-border">
              {filtered.map((ind) => {
                const isOn = selected.includes(ind);
                return (
                  <label
                    key={ind}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${isOn ? "bg-emerald-50 dark:bg-emerald-950/30" : "hover:bg-secondary"}`}
                    data-testid={`interest-row-${ind}`}
                  >
                    <Checkbox
                      checked={isOn}
                      onCheckedChange={() => toggle(ind)}
                      data-testid={`interest-checkbox-${ind}`}
                    />
                    <span className="text-sm font-medium flex-1">{ind}</span>
                    {isOn && <Sparkle size={13} weight="fill" className="text-emerald-600" />}
                  </label>
                );
              })}
              {filtered.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground">No industries match &quot;{q}&quot;</div>
              )}
            </div>
          )}

          <div className="rounded-lg bg-secondary/60 border border-border px-3 py-2.5 text-[11px] text-muted-foreground">
            <span className="font-semibold text-foreground">Tip</span> — new industries are added under{" "}
            <Link to="/admin/filters" onClick={() => onOpenChange(false)} className="text-emerald-700 dark:text-emerald-400 font-semibold hover:underline">
              Admin › Filters
            </Link>. They appear here automatically.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="interests-cancel-btn">Cancel</Button>
          <Button
            onClick={save}
            disabled={busy}
            className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
            data-testid="interests-save-btn"
          >
            {busy ? "Saving…" : "Save interests"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
